import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import {
  normalizeClientTypeFilters,
  sortClientTypes,
} from "./clientTypes";

interface ClientTypeFilterContextType {
  selectedClientTypes: string[] | null;
  setSelectedClientTypes: (types: string[] | null) => void;
  availableClientTypes: string[];
  setAvailableClientTypes: (types: string[]) => void;
  loading: boolean;
  error: string | null;
}

const ClientTypeFilterContext = createContext<ClientTypeFilterContextType | undefined>(
  undefined
);

const SAVE_DEBOUNCE_MS = 500;

function normalizeClientTypes(types: string[] | null): string[] | null {
  return normalizeClientTypeFilters(types);
}

function areStringArraysEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function ClientTypeFilterProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;

  const [selectedClientTypes, setSelectedClientTypesState] = useState<string[] | null>(null);
  const [availableClientTypes, setAvailableClientTypesState] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistSelection = useCallback(
    async (types: string[] | null) => {
      if (!userId) return;

      const normalized = normalizeClientTypes(types);

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ kanban_client_type_filters: normalized })
        .eq("id", userId);

      if (updateError) {
        setError(updateError.message);
      } else {
        setError(null);
      }
    },
    [userId]
  );

  useEffect(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    if (authLoading) return;

    if (!userId) {
      setSelectedClientTypesState(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchProfileSelection = async () => {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from("profiles")
        .select("kanban_client_type_filters")
        .eq("id", userId)
        .maybeSingle();

      if (cancelled) return;

      if (fetchError) {
        setError(fetchError.message);
        setLoading(false);
        return;
      }

      setSelectedClientTypesState(
        normalizeClientTypes((data?.kanban_client_type_filters as string[] | null) ?? null)
      );
      setError(null);
      setLoading(false);
    };

    fetchProfileSelection();

    return () => {
      cancelled = true;
    };
  }, [authLoading, userId]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  const setSelectedClientTypes = useCallback(
    (types: string[] | null) => {
      const normalized = normalizeClientTypes(types);

      setSelectedClientTypesState(normalized);

      if (!userId) return;

      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }

      saveTimerRef.current = setTimeout(() => {
        persistSelection(normalized);
      }, SAVE_DEBOUNCE_MS);
    },
    [persistSelection, userId]
  );

  const setAvailableClientTypes = useCallback((types: string[]) => {
    const normalized = Array.from(new Set(types.map((t) => t.trim()).filter(Boolean))).sort(
      sortClientTypes
    );

    setAvailableClientTypesState((prev) =>
      areStringArraysEqual(prev, normalized) ? prev : normalized
    );
  }, []);

  return (
    <ClientTypeFilterContext.Provider
      value={{
        selectedClientTypes,
        setSelectedClientTypes,
        availableClientTypes,
        setAvailableClientTypes,
        loading,
        error,
      }}
    >
      {children}
    </ClientTypeFilterContext.Provider>
  );
}

export function useClientTypeFilter() {
  const context = useContext(ClientTypeFilterContext);
  if (context === undefined) {
    throw new Error("useClientTypeFilter doit être utilisé dans un ClientTypeFilterProvider");
  }
  return context;
}
